/*
 * customs-declaration
 *
 * Before the cart reaches checkout the buyer must state the goods, what each
 * box is worth, whether it is a sale or a gift, and accept the Terms.
 *
 * Goods is a closed list. As free text buyers answered "gift" or "礼品", which
 * repeats the sale-or-gift question and describes nothing, or a bare quantity
 * — none of it usable on a CN22. "Other" reopens a text box, then mandatory.
 *
 * Value is collected PER BOX, not as one total, because a mainland China order
 * can be split into one parcel per box at checkout and each parcel needs its
 * own CN22 figure. A total cannot be divided after the fact: an 1100 EUR box
 * and a 600 EUR box are not two 850s. Both numbers reach the order — the grand
 * total under the key the admin already knows, the breakdown beside it.
 *
 * Rows are re-rendered on every cart change. This block sits outside
 * .js-contents so Dawn never refreshes it, which is what keeps the buyer's
 * typing safe but would also let the rows go stale against the cart.
 *
 * The checkout button stays clickable on purpose. Greying it out left the
 * buyer nothing to press, and they went looking for another way to pay.
 */
if (!customElements.get('customs-declaration')) {
  const GOODS_ATTR = 'attributes[Customs: goods declared]';

  customElements.define(
    'customs-declaration',
    class CustomsDeclaration extends HTMLElement {
      connectedCallback() {
        this.cartForm = document.getElementById('cart');
        this.goods = this.querySelector('#CustomsGoods');
        this.goodsOther = this.querySelector('#CustomsGoodsOther');
        this.otherField = this.querySelector('[data-customs-other]');
        this.lines = this.querySelector('[data-customs-lines]');
        this.totalOutput = this.querySelector('[data-customs-total]');
        this.totalInput = this.querySelector('[data-customs-total-input]');
        this.breakdownInput = this.querySelector('[data-customs-breakdown-input]');
        this.nature = this.querySelectorAll('input[name="attributes[Customs: sale or gift]"]');
        this.terms = this.querySelector('#TermsAccepted');
        this.blocked = this.querySelector('[data-customs-blocked]');

        this.showErrors = false;
        this.saveTimer = null;
        this.syncTimer = null;

        this.addEventListener('input', this.onChange.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));

        if (this.cartForm) {
          /* `required` stays as the fallback for a browser that never ran this
             script. While it runs we validate ourselves, so the browser's own
             bubbles do not pre-empt the localised messages. */
          this.cartForm.setAttribute('novalidate', 'novalidate');
          this.cartForm.addEventListener('submit', this.onSubmit.bind(this));
        }

        this.watchCart();
        this.restore();
        this.syncGoodsMode();
        this.refresh();
      }

      /* Two ways of hearing about a cart change, because neither is certain:
         newer Dawn publishes cartUpdate, older ones only swap the section. */
      watchCart() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.queueSync());
        }
        const cartItems = document.getElementById('main-cart-items') || document.querySelector('cart-items');
        if (cartItems && 'MutationObserver' in window) {
          new MutationObserver(() => this.queueSync()).observe(cartItems, { childList: true, subtree: true });
        }
      }

      queueSync() {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.syncLines(), 200);
      }

      /* Every figure is remembered twice: under its exact row, and under the
         box title alone. The value describes ONE box, so changing how many of
         them are ordered must not throw the number away. */
      remember(store, title, quantity, value) {
        if (!value) return;
        store.set(this.rowKey(title, quantity), value);
        store.set(this.rowKey(title, null), value);
      }

      recall(store, title, quantity) {
        return store.get(this.rowKey(title, quantity)) || store.get(this.rowKey(title, null)) || '';
      }

      async syncLines() {
        if (!this.lines) return;

        const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        let cart;
        try {
          cart = await fetch(`${root}cart.js`).then((r) => r.json());
        } catch (error) {
          return;
        }
        if (!cart || !Array.isArray(cart.items)) return;

        const kept = new Map();
        this.rows().forEach((row) => {
          const input = row.querySelector('[data-customs-line-value]');
          this.remember(kept, row.dataset.title, row.dataset.quantity, input ? input.value.trim() : '');
        });

        const label = this.lines.dataset.label || '';
        this.lines.innerHTML = '';

        cart.items.forEach((item) => {
          const title = item.product_title || item.title;
          const quantity = String(item.quantity);

          const row = document.createElement('div');
          row.className = 'customs-decl__line';
          row.setAttribute('data-customs-line', '');
          row.dataset.title = title;
          row.dataset.quantity = quantity;

          const name = document.createElement('span');
          name.className = 'customs-decl__line-name';
          name.textContent = item.quantity > 1 ? `${title} × ${item.quantity}` : title;

          const wrap = document.createElement('div');
          wrap.className = 'customs-decl__value-wrap';

          const input = document.createElement('input');
          input.type = 'text';
          input.className = 'customs-decl__input customs-decl__input--value';
          input.setAttribute('data-customs-line-value', '');
          input.inputMode = 'decimal';
          input.autocomplete = 'off';
          input.required = true;
          input.setAttribute('aria-label', label ? `${label} — ${title}` : title);
          input.value = this.recall(kept, title, quantity);

          const currency = document.createElement('span');
          currency.className = 'customs-decl__currency';
          currency.textContent = 'EUR';

          wrap.append(input, currency);
          row.append(name, wrap);
          this.lines.append(row);
        });

        this.refresh();
        this.save();
      }

      rows() {
        return Array.from(this.querySelectorAll('[data-customs-line]'));
      }

      rowKey(title, quantity) {
        return quantity === null ? String(title) : `${title} ${quantity}`;
      }

      /* The stored breakdown is written to be read by a human on the order, so
         it is parsed back rather than kept in a second, hidden format. */
      restore() {
        const stored = this.dataset.breakdown;
        if (!stored) return;

        const known = new Map();
        stored.split(' | ').forEach((entry) => {
          const match = entry.match(/^(.*) × (\d+) = ([\d.]+) EUR$/);
          if (match) this.remember(known, match[1], match[2], match[3]);
        });

        this.rows().forEach((row) => {
          const input = row.querySelector('[data-customs-line-value]');
          if (input && !input.value) input.value = this.recall(known, row.dataset.title, row.dataset.quantity);
        });
      }

      /* The list and the "Other" box share one attribute name, so only one may
         be submittable. The box is disabled while hidden; when it takes over,
         the list gives up its name rather than being disabled itself — a
         disabled list would strand the buyer in "Other" with no way back. */
      syncGoodsMode() {
        if (!this.goods || !this.goodsOther) return;
        const isOther = this.goods.value === 'Other';

        if (this.otherField) this.otherField.hidden = !isOther;
        this.goodsOther.disabled = !isOther;

        if (isOther) this.goods.removeAttribute('name');
        else this.goods.setAttribute('name', GOODS_ATTR);
      }

      /* Buyers type 1450, 1 450, 1,450.00 or 1.450,00. Spaces go, then the last
         separator decides: alone and followed by three digits it is thousands,
         otherwise it is the decimal point. */
      parseAmount(raw) {
        const cleaned = String(raw || '').replace(/[\s ']/g, '');
        if (!cleaned) return NaN;

        const lastComma = cleaned.lastIndexOf(',');
        const lastDot = cleaned.lastIndexOf('.');
        const lastSeparator = Math.max(lastComma, lastDot);
        if (lastSeparator === -1) return Number(cleaned);

        const trailingDigits = cleaned.length - lastSeparator - 1;
        if ((lastComma === -1 || lastDot === -1) && trailingDigits === 3) {
          return Number(cleaned.replace(/[.,]/g, ''));
        }

        const whole = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');
        return Number(`${whole}.${cleaned.slice(lastSeparator + 1)}`);
      }

      collectValues() {
        const parts = [];
        let total = 0;
        let complete = this.rows().length > 0;

        this.rows().forEach((row) => {
          const input = row.querySelector('[data-customs-line-value]');
          const amount = this.parseAmount(input ? input.value : '');
          const quantity = Number(row.dataset.quantity) || 1;

          if (!(amount > 0)) {
            complete = false;
            return;
          }

          total += amount * quantity;
          parts.push(`${row.dataset.title} × ${quantity} = ${amount.toFixed(2)} EUR`);
        });

        return { complete, total, breakdown: parts.join(' | ') };
      }

      collect() {
        const picked = Array.from(this.nature).find((input) => input.checked);
        const category = this.goods ? this.goods.value : '';
        const other = this.goodsOther ? this.goodsOther.value.trim() : '';
        const values = this.collectValues();

        return {
          category,
          other,
          goods: category === 'Other' ? other : category,
          values,
          nature: picked ? picked.value : '',
          terms: this.terms ? this.terms.checked : false,
        };
      }

      errors() {
        const data = this.collect();
        const found = [];

        if (!data.category) found.push(['CustomsGoods', this.dataset.msgGoods]);
        else if (data.category === 'Other' && !data.other) found.push(['CustomsGoodsOther', this.dataset.msgOther]);

        if (!data.values.complete || !(data.values.total > 0)) found.push(['CustomsValue', this.dataset.msgValue]);
        if (!data.nature) found.push(['CustomsNature', this.dataset.msgNature]);
        if (!data.terms) found.push(['TermsAccepted', this.dataset.msgTerms]);
        return found;
      }

      renderErrors(found) {
        const active = new Map(found);
        this.querySelectorAll('[data-error-for]').forEach((node) => {
          const message = this.showErrors ? active.get(node.dataset.errorFor) : undefined;
          node.textContent = message || '';
          node.hidden = !message;
        });
      }

      /* Nothing is disabled here: messages appear only once the buyer has
         actually tried to check out. */
      refresh() {
        const values = this.collectValues();

        if (this.totalOutput) this.totalOutput.textContent = values.total > 0 ? `${values.total.toFixed(2)} EUR` : '—';
        if (this.totalInput) this.totalInput.value = values.total > 0 ? values.total.toFixed(2) : '';
        if (this.breakdownInput) this.breakdownInput.value = values.breakdown;

        const found = this.errors();
        const valid = found.length === 0;

        this.renderErrors(found);
        if (this.blocked) this.blocked.hidden = valid || !this.showErrors;

        return valid;
      }

      onChange(event) {
        if (event && event.target === this.goods) {
          this.syncGoodsMode();
          if (this.goods.value === 'Other' && this.goodsOther) this.goodsOther.focus();
        }

        this.refresh();
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.save(), 500);
      }

      /* Rewrite the amount as a plain number, so what the buyer sees is exactly
         what gets declared. */
      onFocusOut(event) {
        const field = event.target;
        if (!field || !field.hasAttribute('data-customs-line-value')) return;

        const amount = this.parseAmount(field.value);
        if (!(amount > 0)) return;

        const normalised = amount.toFixed(2);
        if (normalised === field.value) return;
        field.value = normalised;
        this.refresh();
        this.save();
      }

      onSubmit(event) {
        this.showErrors = true;
        if (this.refresh()) return;

        event.preventDefault();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const firstError = this.querySelector('.customs-decl__error:not([hidden])');
        const field = firstError ? firstError.closest('.customs-decl__field') : null;
        const focusable = field ? field.querySelector('select, input:not([type="hidden"])') : null;
        if (focusable) focusable.focus({ preventScroll: true });
      }

      save() {
        const data = this.collect();
        const body = JSON.stringify({
          attributes: {
            'Customs: goods declared': data.goods,
            'Customs: declared value (EUR)': data.values.total > 0 ? data.values.total.toFixed(2) : '',
            'Customs: declared value per box': data.values.breakdown,
            'Customs: sale or gift': data.nature,
            'Terms accepted': data.terms && this.terms ? this.terms.value : '',
          },
        });
        const url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

        fetch(url, { ...config, body }).catch(() => {});
      }
    }
  );
}
